import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Heart, MessageCircle, Share2, Calendar, User, Eye, ArrowLeft,
  Facebook, Twitter, Linkedin, Mail, Link as LinkIcon, Instagram
} from 'lucide-react';
import TreeLogo from '@/components/TreeLogo';
import TreeLoadingSpinner from '@/components/TreeLoadingSpinner';

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [commentName, setCommentName] = useState('');
  const [commentContent, setCommentContent] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/api/blog/${slug}`);
      setPost(response.data);
    } catch (error) {
      console.error('Error fetching post:', error);
      toast.error('Post not found');
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    if (liked) return;
    try {
      await axios.post(`${API}/api/blog/${post.post_id}/like`);
      setLiked(true);
      setPost(p => ({ ...p, likes_count: p.likes_count + 1 }));
      toast.success('Thanks for the love!');
    } catch (error) {
      toast.error('Failed to like post');
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!commentName.trim() || !commentContent.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmittingComment(true);
    try {
      await axios.post(`${API}/api/blog/${post.post_id}/comment`, {
        post_id: post.post_id,
        author_name: commentName.trim(),
        content: commentContent.trim()
      });
      toast.success('Comment submitted for moderation');
      setCommentName('');
      setCommentContent('');
    } catch (error) {
      toast.error('Failed to submit comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleAdClick = async (ad) => {
    try {
      const response = await axios.get(`${API}/api/blog/ad/${ad.ad_id}/click`);
      window.open(response.data.link_url, '_blank');
    } catch (error) {
      window.open(ad.link_url, '_blank');
    }
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = post?.title || '';

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`,
    linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareTitle)}`,
    instagram: `https://www.instagram.com/`, // Instagram doesn't support direct sharing, opens profile
    email: `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(`Check out this article: ${shareUrl}`)}`
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success('Link copied to clipboard!');
    setShowShareMenu(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <TreeLoadingSpinner size="lg" />
      </div>
    );
  }

  if (!post) return null;

  const sidebarAds = post.ads?.filter(ad => ad.position === 'sidebar') || [];
  const bottomAds = post.ads?.filter(ad => ad.position === 'bottom') || [];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'hsl(45 25% 97%)' }}>
      {/* SEO Meta Tags would be handled by React Helmet in production */}
      
      {/* Navigation */}
      <nav className="sticky top-0 z-50" style={{ backgroundColor: 'hsl(220 15% 55%)' }}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'hsl(15 40% 82%)' }}>
              <TreeLogo size={24} className="text-slate-600" />
            </div>
            <span className="text-lg text-white font-light" style={{ fontFamily: 'Playfair Display, serif' }}>
              Rooted Blog
            </span>
          </Link>
          
          <Button 
            variant="ghost" 
            className="text-white/80 hover:text-white"
            onClick={() => navigate('/blog')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            All Posts
          </Button>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <article className="lg:col-span-2">
            {/* Featured Image */}
            {post.featured_image && (
              <div className="aspect-video rounded-2xl overflow-hidden mb-8">
                <img 
                  src={post.featured_image} 
                  alt={post.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Tags */}
            {post.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {post.tags.map(tag => (
                  <Badge key={tag} variant="secondary" className="cursor-pointer" onClick={() => navigate(`/blog?tag=${tag}`)}>
                    {tag}
                  </Badge>
                ))}
              </div>
            )}

            {/* Title & Subtitle */}
            <h1 className="text-3xl md:text-4xl mb-3" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(220 15% 25%)' }}>
              {post.title}
            </h1>
            {post.subtitle && (
              <p className="text-xl mb-6" style={{ color: 'hsl(220 10% 50%)' }}>
                {post.subtitle}
              </p>
            )}

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 mb-8 pb-8 border-b" style={{ borderColor: 'hsl(220 10% 90%)' }}>
              {post.author && (
                <Link 
                  to={`/author/${post.author.author_id}`}
                  className="flex items-center gap-2 hover:opacity-80"
                >
                  {post.author.photo_url ? (
                    <img src={post.author.photo_url} alt={post.author.name} className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'hsl(15 40% 85%)' }}>
                      <User className="w-5 h-5" style={{ color: 'hsl(220 15% 40%)' }} />
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-sm" style={{ color: 'hsl(220 15% 30%)' }}>{post.author.name}</p>
                    {post.author.affiliation && (
                      <p className="text-xs" style={{ color: 'hsl(220 10% 55%)' }}>{post.author.affiliation}</p>
                    )}
                  </div>
                </Link>
              )}
              
              <span className="flex items-center gap-1 text-sm" style={{ color: 'hsl(220 10% 55%)' }}>
                <Calendar className="w-4 h-4" />
                {formatDate(post.published_at)}
              </span>
              
              <span className="flex items-center gap-1 text-sm" style={{ color: 'hsl(220 10% 55%)' }}>
                <Eye className="w-4 h-4" />
                {post.views_count} views
              </span>
            </div>

            {/* Content */}
            <div 
              className="prose prose-lg max-w-none mb-8"
              style={{ color: 'hsl(220 10% 30%)' }}
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            {/* Engagement Bar */}
            <div className="flex items-center justify-between py-6 border-t border-b mb-8" style={{ borderColor: 'hsl(220 10% 90%)' }}>
              <div className="flex items-center gap-4">
                <Button
                  variant={liked ? "default" : "outline"}
                  size="sm"
                  onClick={handleLike}
                  className="rounded-full"
                  data-testid="like-btn"
                >
                  <Heart className={`w-4 h-4 mr-2 ${liked ? 'fill-current' : ''}`} />
                  {post.likes_count} Likes
                </Button>
                
                <span className="flex items-center gap-1 text-sm" style={{ color: 'hsl(220 10% 55%)' }}>
                  <MessageCircle className="w-4 h-4" />
                  {post.comments?.length || 0} Comments
                </span>
              </div>

              {/* Share Menu */}
              <div className="relative">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowShareMenu(!showShareMenu)}
                  className="rounded-full"
                  data-testid="share-btn"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
                
                {showShareMenu && (
                  <div className="absolute right-0 mt-2 p-3 bg-white rounded-xl shadow-lg border z-10" style={{ minWidth: '200px' }}>
                    <p className="text-xs font-medium mb-2" style={{ color: 'hsl(220 10% 50%)' }}>Share this article</p>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="ghost" className="p-2" onClick={() => window.open(shareLinks.facebook, '_blank')}>
                        <Facebook className="w-5 h-5 text-blue-600" />
                      </Button>
                      <Button size="sm" variant="ghost" className="p-2" onClick={() => window.open(shareLinks.twitter, '_blank')}>
                        <Twitter className="w-5 h-5 text-sky-500" />
                      </Button>
                      <Button size="sm" variant="ghost" className="p-2" onClick={() => window.open(shareLinks.linkedin, '_blank')}>
                        <Linkedin className="w-5 h-5 text-blue-700" />
                      </Button>
                      <Button size="sm" variant="ghost" className="p-2" onClick={() => window.open(shareLinks.instagram, '_blank')}>
                        <Instagram className="w-5 h-5 text-pink-600" />
                      </Button>
                      <Button size="sm" variant="ghost" className="p-2" onClick={() => window.open(shareLinks.email, '_blank')}>
                        <Mail className="w-5 h-5 text-gray-600" />
                      </Button>
                      <Button size="sm" variant="ghost" className="p-2" onClick={copyLink}>
                        <LinkIcon className="w-5 h-5 text-gray-600" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Bottom Ads */}
            {bottomAds.length > 0 && (
              <div className="mb-8 flex flex-wrap gap-4 justify-center">
                {bottomAds.map(ad => (
                  <div 
                    key={ad.ad_id} 
                    className="cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => handleAdClick(ad)}
                  >
                    <img src={ad.image_url} alt={ad.name} className="rounded-lg max-h-24" />
                  </div>
                ))}
              </div>
            )}

            {/* Comments Section */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold mb-6" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(220 15% 30%)' }}>
                Comments ({post.comments?.length || 0})
              </h3>

              {/* Comment Form */}
              <Card className="p-6 mb-6">
                <form onSubmit={handleSubmitComment}>
                  <h4 className="font-medium mb-4" style={{ color: 'hsl(220 15% 35%)' }}>Leave a Comment</h4>
                  <div className="space-y-4">
                    <Input
                      placeholder="Your name (required)"
                      value={commentName}
                      onChange={(e) => setCommentName(e.target.value)}
                      required
                      data-testid="comment-name"
                    />
                    <Textarea
                      placeholder="Share your thoughts..."
                      value={commentContent}
                      onChange={(e) => setCommentContent(e.target.value)}
                      rows={4}
                      required
                      data-testid="comment-content"
                    />
                    <Button type="submit" disabled={submittingComment} data-testid="submit-comment">
                      {submittingComment ? 'Submitting...' : 'Submit Comment'}
                    </Button>
                    <p className="text-xs" style={{ color: 'hsl(220 10% 60%)' }}>
                      Comments are moderated and will appear after approval.
                    </p>
                  </div>
                </form>
              </Card>

              {/* Existing Comments */}
              <div className="space-y-4">
                {post.comments?.map(comment => (
                  <Card key={comment.comment_id} className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'hsl(15 40% 85%)' }}>
                        <User className="w-5 h-5" style={{ color: 'hsl(220 15% 40%)' }} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm" style={{ color: 'hsl(220 15% 30%)' }}>
                            {comment.author_name}
                          </span>
                          <span className="text-xs" style={{ color: 'hsl(220 10% 60%)' }}>
                            {formatDate(comment.created_at)}
                          </span>
                        </div>
                        <p className="text-sm" style={{ color: 'hsl(220 10% 40%)' }}>
                          {comment.content}
                        </p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            {/* Related Posts */}
            {post.related_posts?.length > 0 && (
              <div>
                <h3 className="text-xl font-semibold mb-6" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(220 15% 30%)' }}>
                  Related Articles
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  {post.related_posts.map(related => (
                    <Card 
                      key={related.post_id}
                      className="p-4 cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => navigate(`/blog/${related.slug}`)}
                    >
                      {related.featured_image && (
                        <img src={related.featured_image} alt={related.title} className="rounded-lg mb-3 aspect-video object-cover" />
                      )}
                      <h4 className="font-medium text-sm" style={{ color: 'hsl(220 15% 30%)' }}>
                        {related.title}
                      </h4>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </article>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            {/* Author Card */}
            {post.author && (
              <Card className="p-6 mb-6">
                <Link to={`/author/${post.author.author_id}`} className="block text-center">
                  {post.author.photo_url ? (
                    <img src={post.author.photo_url} alt={post.author.name} className="w-20 h-20 rounded-full mx-auto mb-4 object-cover" />
                  ) : (
                    <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: 'hsl(15 40% 85%)' }}>
                      <User className="w-10 h-10" style={{ color: 'hsl(220 15% 40%)' }} />
                    </div>
                  )}
                  <h4 className="font-semibold" style={{ color: 'hsl(220 15% 30%)' }}>{post.author.name}</h4>
                  {post.author.affiliation && (
                    <p className="text-sm mb-2" style={{ color: 'hsl(220 10% 55%)' }}>{post.author.affiliation}</p>
                  )}
                  {post.author.bio && (
                    <p className="text-sm" style={{ color: 'hsl(220 10% 45%)' }}>{post.author.bio}</p>
                  )}
                </Link>
              </Card>
            )}

            {/* Sidebar Ads */}
            {sidebarAds.length > 0 && (
              <div className="space-y-4 mb-6">
                {sidebarAds.map(ad => (
                  <Card 
                    key={ad.ad_id} 
                    className="overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => handleAdClick(ad)}
                  >
                    <img src={ad.image_url} alt={ad.name} className="w-full" />
                  </Card>
                ))}
              </div>
            )}

            {/* Subscribe CTA */}
            <Card className="p-6 text-center" style={{ backgroundColor: 'hsl(250 12% 92%)' }}>
              <h4 className="font-semibold mb-2" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(220 15% 30%)' }}>
                Start Your Journey
              </h4>
              <p className="text-sm mb-4" style={{ color: 'hsl(220 10% 45%)' }}>
                Get daily devotionals delivered to your inbox.
              </p>
              <Button onClick={() => navigate('/subscribe')} className="w-full">
                Subscribe Now
              </Button>
            </Card>
          </aside>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 px-6 text-center" style={{ backgroundColor: 'hsl(220 15% 55%)' }}>
        <p className="text-white/70 text-sm">
          © {new Date().getFullYear()} Rooted Faith Journal. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default BlogPostPage;
